import { useEffect, useState } from "react";
import { Line } from "react-chartjs-2";

import {
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  TimeScale,
  Tooltip,
} from "chart.js";

import { formatDateLabels } from "../graphing/graphUtils";

// Register chart elements
ChartJS.register(
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
  Filler,
);

// Flatten nested numbers into dot notation
const flattenRecord = (obj, prefix = "", result = {}) => {
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (value && typeof value === "object" && !Array.isArray(value)) {
      flattenRecord(value, path, result);
    } else if (typeof value === "number") {
      result[path] = value;
    }
  }
  return result;
};

const BotpressBotUsageLineChart = ({ records, filters = [], metricVisibility = {} }) => {
  const [chartData, setChartData] = useState(null);

  useEffect(() => {
    if (!records || !records.length) return;

    // Sort records by date
    const sorted = [...records].sort(
      (a, b) => new Date(a.startDateTimeUtc) - new Date(b.startDateTimeUtc),
    );

    // Flatten each record for easier dataset mapping
    const flattened = sorted.map(r => flattenRecord(r));

    // Build datasets only for visible metrics
    const datasets = filters
      .filter(f => metricVisibility[f.metric]) // only visible metrics
      .map(f => ({
        label: f.label,
        data: flattened.map(record => record[f.metric] ?? 0),
        borderColor: f.color,
        backgroundColor: f.color,
        borderWidth: 2,
        pointRadius: 3,
        fill: false,
        tension: 0.3,
      }));

    let labels = sorted.map(r => new Date(r.startDateTimeUtc));

    labels = formatDateLabels(labels);

    setChartData({ labels, datasets });
  }, [records, filters, metricVisibility]);

  if (!chartData) return <p>No data available.</p>;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false, // We'll handle visibility through MetricBox toggles
      },
    },
  };

  return <Line data={chartData} options={options} />;
};

export default BotpressBotUsageLineChart;
